import { useState, type FormEvent } from "react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import type { BrandExtractionResult } from "../../../../shared/types";
import { useExtractBrand, useCreateBrand } from "../../hooks/useBrand";
import { api } from "../../lib/api";

/** Prefer the extracted tone phrase; fall back to the descriptor list. */
function extractedTone(result: BrandExtractionResult): string {
  return result.tone?.trim() || (result.tone_descriptors ?? []).join(", ");
}

interface BrandExtractionProps {
  onComplete?: (brandId: string, brandName: string) => void;
}

export function BrandExtraction({ onComplete }: BrandExtractionProps) {
  const [url, setUrl] = useState("");
  const [result, setResult] = useState<BrandExtractionResult | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdBrandId, setCreatedBrandId] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [manualName, setManualName] = useState("");
  const [manualIndustry, setManualIndustry] = useState("");
  const [manualAudience, setManualAudience] = useState("");
  const [manualError, setManualError] = useState<string | null>(null);
  const extract = useExtractBrand();
  const createBrand = useCreateBrand();

  async function handleExtract(e: FormEvent) {
    e.preventDefault();
    try {
      const data = await extract.mutateAsync(url);
      setResult(data);
    } catch {
      // The mutation owns the visible error state; consume the rejection so the
      // async form handler does not leak an unhandled promise rejection.
    }
  }

  async function handleConfirm() {
    if (!result || isConfirming) return;
    setSaveError(null);
    setCreateError(null);
    setIsConfirming(true);
    try {
      let brandId = createdBrandId;
      if (!brandId) {
        try {
          brandId = (await createBrand.mutateAsync({
            name: result.brand_name,
            industry: result.industry,
            target_audience: result.target_audience,
            website_url: url,
            ...(result.positioning_statement ? { positioning_statement: result.positioning_statement } : {}),
          })).id;
          setCreatedBrandId(brandId);
        } catch (error) {
          setCreateError(error instanceof Error ? error.message : "The create request failed.");
          return;
        }
      }

      try {
        await api.put(`/api/brands/${brandId}/voice`, {
          tone: extractedTone(result),
          tone_descriptors: result.tone_descriptors ?? [],
          vocabulary: result.vocabulary ?? [],
          example_posts: result.example_voice_samples ?? [],
          guidelines: result.positioning_statement ?? "",
        });
        setCreatedBrandId(null);
        onComplete?.(brandId, result.brand_name);
      } catch (error) {
        setSaveError(
          error instanceof Error
            ? `Brand created, but voice settings could not be saved yet: ${error.message}`
            : "Brand created, but voice settings could not be saved yet.",
        );
      }
    } finally {
      setIsConfirming(false);
    }
  }

  async function handleManualCreate(e: FormEvent) {
    e.preventDefault();
    if (isConfirming) return;
    setManualError(null);
    setIsConfirming(true);

    try {
      const brand = await createBrand.mutateAsync({
        name: manualName,
        industry: manualIndustry,
        target_audience: manualAudience,
        website_url: url || null,
      });
      onComplete?.(brand.id, manualName);
    } catch (error) {
      setManualError(
        error instanceof Error
          ? `Could not create brand yet: ${error.message}`
          : "Could not create brand yet.",
      );
    } finally {
      setIsConfirming(false);
    }
  }

  if (result) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Review extracted details</h2>
          <p className="mt-1 text-sm text-slate-600">Confirm the profile before creating the brand.</p>
        </div>
        <div className="space-y-3 rounded-lg border border-slate-200 p-4">
          <div>
            <span className="text-xs font-medium uppercase text-slate-400">Brand</span>
            <p className="text-sm font-medium text-slate-900">{result.brand_name}</p>
          </div>
          <div>
            <span className="text-xs font-medium uppercase text-slate-400">Industry</span>
            <p className="text-sm text-slate-700">{result.industry}</p>
          </div>
          <div>
            <span className="text-xs font-medium uppercase text-slate-400">Target Audience</span>
            <p className="text-sm text-slate-700">{result.target_audience}</p>
          </div>
          <div>
            <span className="text-xs font-medium uppercase text-slate-400">Tone</span>
            {result.tone?.trim() && (
              <p className="text-sm text-slate-700">{result.tone.trim()}</p>
            )}
            <div className="mt-1 flex flex-wrap gap-2">
              {(result.tone_descriptors ?? []).length > 0 ? (
                (result.tone_descriptors ?? []).map((tone) => (
                  <span key={tone} className="rounded-full bg-brand-50 px-3 py-1 text-xs text-brand-700">
                    {tone}
                  </span>
                ))
              ) : !extractedTone(result) ? (
                <span className="text-xs text-slate-400">No tone extracted — you can set it later in Brand Settings</span>
              ) : null}
            </div>
          </div>
          <div>
            <span className="text-xs font-medium uppercase text-slate-400">Positioning</span>
            <p className="text-sm text-slate-700">{result.positioning_statement}</p>
          </div>
        </div>
        {saveError && (
          <div role="status" aria-live="polite" className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <p>{saveError}</p>
            <p>Retry voice save now, or continue and finish voice settings later from Brand Settings.</p>
          </div>
        )}
        {createError && (
          <div role="alert" className="space-y-1 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <p className="font-medium">We couldn't create this brand yet.</p>
            <p>Your extracted details are still here. {createError}</p>
          </div>
        )}
        <div className="flex gap-3">
          <Button onClick={handleConfirm} disabled={isConfirming || createBrand.isPending}>
            {isConfirming ? (createdBrandId ? "Saving voice…" : "Creating…") : saveError ? "Retry voice save" : createError ? "Retry creating brand" : "Confirm and create"}
          </Button>
          {saveError && createdBrandId && onComplete && (
            <Button variant="secondary" onClick={() => onComplete(createdBrandId, result.brand_name)}>
              Continue anyway
            </Button>
          )}
          {!createdBrandId ? (
            <Button
              variant="secondary"
              onClick={() => {
                setResult(null);
                setSaveError(null);
                setCreateError(null);
              }}
            >
              Start over
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  if (manualMode) {
    return (
      <form onSubmit={handleManualCreate} className="space-y-4">
        <p className="text-sm text-slate-500">
          Enter the minimum information the agent needs.
        </p>
        <Input
          id="brand-name"
          label="Brand Name"
          value={manualName}
          onChange={(e) => setManualName(e.target.value)}
          placeholder="HelloConvo"
          required
        />
        <Input
          id="brand-industry"
          label="Industry"
          value={manualIndustry}
          onChange={(e) => setManualIndustry(e.target.value)}
          placeholder="AI marketing"
          required
        />
        <Input
          id="brand-target-audience"
          label="Target Audience"
          value={manualAudience}
          onChange={(e) => setManualAudience(e.target.value)}
          placeholder="Founders and small teams"
          required
        />
        <Input
          id="brand-website-url"
          label="Website URL (optional)"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://yoursite.com"
        />
        {manualError && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <p className="font-medium">We couldn't create this brand yet.</p>
            <p className="mt-1">Your entered details are still here. {manualError}</p>
          </div>
        )}
        <div className="flex gap-3">
          <Button type="submit" disabled={isConfirming || createBrand.isPending}>
            {isConfirming || createBrand.isPending ? "Creating..." : manualError ? "Retry creating brand" : "Create brand"}
          </Button>
          <Button type="button" variant="secondary" onClick={() => setManualMode(false)}>
            Use website extraction
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleExtract} className="space-y-4">
      <p className="text-sm text-slate-500">
        We'll use the site to prepare a profile for your review.
      </p>
      <Input
        id="brand-extract-url"
        label="Website URL"
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://yoursite.com"
        required
      />
      {extract.isError && (
        <div role="alert" className="space-y-1 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <p className="font-medium">Couldn't extract this site automatically.</p>
          <p>
            {extract.error instanceof Error && extract.error.message
              ? extract.error.message
              : "Check the URL and try again, or use 'Create manually instead' below."}
          </p>
        </div>
      )}
      <Button type="submit" disabled={extract.isPending}>
        {extract.isPending ? "Extracting…" : "Extract brand"}
      </Button>
      {extract.isPending && (
        <p className="text-xs text-slate-500">
          Rendering the page in a real browser — this can take 10–30 seconds for content-rich or JS-heavy sites. Hang on.
        </p>
      )}
      <div>
        <Button type="button" variant="secondary" onClick={() => setManualMode(true)}>
          Create manually instead
        </Button>
      </div>
    </form>
  );
}
